import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, X, Send, Loader2, Radio, Square, Settings } from "lucide-react";
import api, { API } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import AudioSettingsModal, {
    getSelectedInputId,
    getSelectedOutputId,
} from "@/components/AudioSettingsModal";

/**
 * VIRTUS Voice Orb
 * ----------------
 * - Fixed bottom-right widget, always visible while authenticated.
 * - "Hey Virtus" wake word (Web Speech API, uses OS default mic).
 * - After wake / manual trigger: 5s recording using the selected mic → Whisper → GPT-5.2 → TTS.
 * - Voice output uses server-side OpenAI TTS (mp3) played through an <audio> element
 *   whose sink is set to the selected speaker via HTMLAudioElement.setSinkId().
 * - Voice command interception: "expand task N", "show task N", "close task", "run brief".
 * - Daily 10:00 morning briefing (auto once/day) plus manual button.
 */

// -- Constants -------------------------------------------------------------
const BRIEFING_HOUR = 10;
const BRIEFING_MINUTE = 0;
// sessionStorage (not localStorage) so nothing sensitive-adjacent lingers past
// tab close. Kiosk stays open all day, so once-per-day still works.
const BRIEFING_KEY = "virtus_last_briefing_date";

const EXPAND_TASK_RE = /\b(?:expand|show|open|pull up|display)\s+task\s+#?\s*(\d+)/i;
const EXPAND_ORDER_RE = /\b(?:expand|show|open|pull up|display)\s+order\s+#?\s*(\d+)/i;
const EXPAND_VESSEL_RE = /\b(?:expand|show|open|status(?:\s+of)?|pull up|display)\s+vessel\s+#?\s*(\d+)/i;
const EXPAND_TRIAL_RE = /\b(?:expand|show|open|pull up|display)\s+trial\s+#?\s*(\d+)/i;
const EXPAND_DEAL_RE = /\b(?:expand|show|open|pull up|display)\s+deal\s+#?\s*(\d+)/i;
const TRACK_ORDER_RE = /\b(?:track|status(?:\s+of)?|where(?:'s|\s+is))\s+order\s+#?\s*(\d+)/i;
const DELIVERED_RE = /\b(?:mark|set)\s+order\s+#?\s*(\d+)\s+(?:as\s+)?delivered/i;
const VESSEL_STATE_RE = /\b(?:mark|set)\s+vessel\s+#?\s*(\d+)\s+(?:as\s+)?(on[- ]?station|transiting|deployed|in[- ]?maintenance|offline|lost[- ]?comms)/i;
const ADVANCE_DEAL_RE = /\b(?:advance|progress|move)\s+deal\s+#?\s*(\d+)/i;
const CLOSE_DEAL_RE = /\b(?:close|mark)\s+deal\s+#?\s*(\d+)\s+(?:as\s+)?(won|lost)/i;
const ADD_ORDER_RE = /\b(?:add|create|new)\s+(?:a\s+|an\s+)?order/i;
const ADD_VESSEL_RE = /\b(?:add|create|new|register)\s+(?:a\s+|an\s+)?vessel/i;
const ADD_TRIAL_RE = /\b(?:add|create|new|log)\s+(?:a\s+|an\s+)?trial/i;
const ADD_DEAL_RE = /\b(?:add|create|new)\s+(?:a\s+|an\s+)?deal/i;
const CLOSE_RE = /\b(?:close|dismiss|hide|collapse|exit)\b/i;
const BRIEF_RE = /\b(?:run|start|give me|do)\s+(?:the\s+)?(?:brief|briefing|morning\s+brief)/i;
const NAV_RE = /\b(?:show|go to|open|navigate to|take me to)\s+(dev\s*plan|development\s*plan|development|operations|ops|production|screen\s*saver|screensaver|logistics|fleet|trials|bd(?:\s*pipeline)?|pipeline|admin(?:\s*users)?|hub|home|main)/i;

const NAV_ROUTES = {
    "devplan": "/dev-plan",
    "development": "/dev-plan",
    "developmentplan": "/dev-plan",
    "operations": "/operations",
    "ops": "/operations",
    "production": "/production",
    "screensaver": "/screensaver",
    "logistics": "/logistics",
    "fleet": "/fleet",
    "trials": "/trials",
    "bd": "/bd",
    "bdpipeline": "/bd",
    "pipeline": "/bd",
    "admin": "/admin/users",
    "adminusers": "/admin/users",
    "hub": "/hub",
    "home": "/hub",
    "main": "/hub",
};

// -- Helpers ---------------------------------------------------------------
function chunkForSpeech(text, maxLen = 3800) {
    // OpenAI TTS is capped at 4096 chars; use a comfortable slice.
    if (!text) return [];
    if (text.length <= maxLen) return [text];
    const parts = text.split(/(?<=[.?!])\s+/);
    const out = [];
    let buf = "";
    for (const s of parts) {
        if ((buf + " " + s).length > maxLen && buf) {
            out.push(buf.trim());
            buf = s;
        } else {
            buf = (buf + " " + s).trim();
        }
    }
    if (buf) out.push(buf.trim());
    return out;
}

async function playBlobOnSelectedSpeaker(blob, audioRef, outputId) {
    const url = URL.createObjectURL(blob);
    audioRef.current = new Audio(url);
    if (outputId && typeof audioRef.current.setSinkId === "function") {
        try {
            await audioRef.current.setSinkId(outputId);
        } catch (e) {
            // Silent fallback to OS default
        }
    }
    return new Promise((resolve) => {
        audioRef.current.onended = () => {
            URL.revokeObjectURL(url);
            resolve();
        };
        audioRef.current.onerror = () => {
            URL.revokeObjectURL(url);
            resolve();
        };
        audioRef.current.play().catch(() => resolve());
    });
}

// -- Component -------------------------------------------------------------
export default function VoiceOrb() {
    const [state, setState] = useState("idle"); // idle | listening | recording | thinking | speaking
    const [expanded, setExpanded] = useState(false);
    const [wakeArmed, setWakeArmed] = useState(false);
    const [text, setText] = useState("");
    const [history, setHistory] = useState([]);
    const [briefingActive, setBriefingActive] = useState(false);
    const [showAudioSettings, setShowAudioSettings] = useState(false);
    const navigate = useNavigate();

    const wakeRecogRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const sessionRef = useRef(null);
    const audioRef = useRef(null);
    const cancelledRef = useRef(false);
    const busyRef = useRef(false); // true while speak() loop is running
    const briefingActiveRef = useRef(false); // hard lock for briefing re-entry

    // -- Speech playback via server TTS + selected speaker
    const stopSpeech = useCallback(() => {
        cancelledRef.current = true;
        if (audioRef.current) {
            try {
                audioRef.current.pause();
                audioRef.current.src = "";
            } catch (_) {}
            audioRef.current = null;
        }
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
            try {
                window.speechSynthesis.cancel();
            } catch (_) {}
        }
        busyRef.current = false;
        briefingActiveRef.current = false;
        setBriefingActive(false);
        setState("idle");
    }, []);

    const speak = useCallback(async (message, onDone) => {
        if (!message) {
            onDone && onDone();
            return;
        }
        // Hard cut any in-flight speech first — no overlapping VIRTUS voice.
        if (busyRef.current) {
            cancelledRef.current = true;
            if (audioRef.current) {
                try {
                    audioRef.current.pause();
                    audioRef.current.src = "";
                } catch (_) {}
                audioRef.current = null;
            }
            // Yield so the paused chunk resolves and exits its await.
            await new Promise((r) => setTimeout(r, 120));
        }
        busyRef.current = true;
        cancelledRef.current = false;
        setState("speaking");
        const outputId = getSelectedOutputId();
        const chunks = chunkForSpeech(message);

        for (const chunk of chunks) {
            if (cancelledRef.current) break;
            try {
                const res = await api.post(
                    "/voice/tts",
                    { text: chunk, voice: "onyx", model: "tts-1" },
                    { responseType: "blob" }
                );
                if (cancelledRef.current) break;
                await playBlobOnSelectedSpeaker(res.data, audioRef, outputId);
            } catch (e) {
                if (cancelledRef.current) break;
                // Fallback to browser SpeechSynthesis if server TTS fails
                if ("speechSynthesis" in window) {
                    await new Promise((resolve) => {
                        const u = new SpeechSynthesisUtterance(chunk);
                        u.rate = 1.02;
                        u.pitch = 0.95;
                        u.onend = resolve;
                        u.onerror = resolve;
                        window.speechSynthesis.speak(u);
                    });
                }
            }
        }

        busyRef.current = false;
        setState("idle");
        onDone && onDone();
    }, []);

    // -- Command interception (returns true if handled locally)
    const tryHandleCommand = useCallback(
        (utterance) => {
            const s = utterance.trim();

            // Navigation
            const navMatch = s.match(NAV_RE);
            if (navMatch) {
                const target = navMatch[1].toLowerCase().replace(/\s+/g, "");
                const route = NAV_ROUTES[target];
                if (route) {
                    navigate(route);
                    const ack = `Opening ${target.replace(/([a-z])([A-Z])/g, "$1 $2")}.`;
                    setHistory((h) => [...h, { role: "assistant", text: ack }]);
                    speak(ack);
                    return true;
                }
            }

            // Expand task
            const expandTaskMatch = s.match(EXPAND_TASK_RE);
            if (expandTaskMatch) {
                const n = parseInt(expandTaskMatch[1], 10);
                window.dispatchEvent(
                    new CustomEvent("virtus:expand-task", { detail: { index: n } })
                );
                const ack = `Expanding task ${n}.`;
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }

            // Expand order
            const expandOrderMatch = s.match(EXPAND_ORDER_RE);
            if (expandOrderMatch) {
                const n = parseInt(expandOrderMatch[1], 10);
                window.dispatchEvent(
                    new CustomEvent("virtus:expand-order", { detail: { index: n } })
                );
                const ack = `Expanding order ${n}.`;
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }

            // Expand vessel
            const expandVesselMatch = s.match(EXPAND_VESSEL_RE);
            if (expandVesselMatch) {
                const n = parseInt(expandVesselMatch[1], 10);
                window.dispatchEvent(
                    new CustomEvent("virtus:expand-vessel", { detail: { index: n } })
                );
                const ack = `Expanding vessel ${n}.`;
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }

            // Expand trial
            const expandTrialMatch = s.match(EXPAND_TRIAL_RE);
            if (expandTrialMatch) {
                const n = parseInt(expandTrialMatch[1], 10);
                window.dispatchEvent(
                    new CustomEvent("virtus:expand-trial", { detail: { index: n } })
                );
                const ack = `Expanding trial ${n}.`;
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }

            // Expand deal
            const expandDealMatch = s.match(EXPAND_DEAL_RE);
            if (expandDealMatch) {
                const n = parseInt(expandDealMatch[1], 10);
                window.dispatchEvent(
                    new CustomEvent("virtus:expand-deal", { detail: { index: n } })
                );
                const ack = `Expanding deal ${n}.`;
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }

            // Set vessel state (mark vessel N as offline / on-station / etc.)
            const vesselStateMatch = s.match(VESSEL_STATE_RE);
            if (vesselStateMatch) {
                const n = parseInt(vesselStateMatch[1], 10);
                const stateSlug = vesselStateMatch[2]
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace("onstation", "on-station")
                    .replace("inmaintenance", "in-maintenance")
                    .replace("lostcomms", "lost-comms");
                (async () => {
                    try {
                        const { data } = await api.post(
                            `/fleet/vessels/by-index/${n}/set-state/${stateSlug}`
                        );
                        const msg = `Vessel ${n}, ${data.callsign}, marked ${stateSlug.replace(/-/g, " ")}.`;
                        setHistory((h) => [...h, { role: "assistant", text: msg }]);
                        speak(msg);
                        window.dispatchEvent(new CustomEvent("virtus:vessels-refreshed"));
                    } catch (e) {
                        const detail = e.response?.data?.detail || `Vessel ${n} not found.`;
                        setHistory((h) => [...h, { role: "assistant", text: detail }]);
                        speak(detail);
                    }
                })();
                return true;
            }

            // Advance deal
            const advanceMatch = s.match(ADVANCE_DEAL_RE);
            if (advanceMatch) {
                const n = parseInt(advanceMatch[1], 10);
                (async () => {
                    try {
                        const { data } = await api.post(`/bd/deals/by-index/${n}/advance`);
                        const msg = `Deal ${n}, ${data.client_name}, advanced to ${data.stage.replace(/-/g, " ")}.`;
                        setHistory((h) => [...h, { role: "assistant", text: msg }]);
                        speak(msg);
                        window.dispatchEvent(new CustomEvent("virtus:deals-refreshed"));
                    } catch (e) {
                        const detail = e.response?.data?.detail || `Deal ${n} not accessible.`;
                        setHistory((h) => [...h, { role: "assistant", text: detail }]);
                        speak(detail);
                    }
                })();
                return true;
            }

            // Close deal as won/lost
            const closeDealMatch = s.match(CLOSE_DEAL_RE);
            if (closeDealMatch) {
                const n = parseInt(closeDealMatch[1], 10);
                const outcome = closeDealMatch[2].toLowerCase();
                (async () => {
                    try {
                        const { data } = await api.post(
                            `/bd/deals/by-index/${n}/close/${outcome}`
                        );
                        const msg = `Deal ${n}, ${data.client_name}, closed ${outcome}.`;
                        setHistory((h) => [...h, { role: "assistant", text: msg }]);
                        speak(msg);
                        window.dispatchEvent(new CustomEvent("virtus:deals-refreshed"));
                    } catch (e) {
                        const detail = e.response?.data?.detail || `Deal ${n} not accessible.`;
                        setHistory((h) => [...h, { role: "assistant", text: detail }]);
                        speak(detail);
                    }
                })();
                return true;
            }

            // Track order (async — makes a live API call and speaks the result)
            const trackMatch = s.match(TRACK_ORDER_RE);
            if (trackMatch) {
                const n = parseInt(trackMatch[1], 10);
                (async () => {
                    setState("thinking");
                    try {
                        const { data } = await api.post(
                            `/logistics/orders/by-index/${n}/track`
                        );
                        const status = (data.status || "unknown").replace(/_/g, " ");
                        const eta = data.expected_delivery || "no ETA on file";
                        const msg = `Order ${n}, ${data.item}. Status: ${status}. Expected delivery ${eta}.`;
                        setHistory((h) => [...h, { role: "assistant", text: msg }]);
                        speak(msg);
                        window.dispatchEvent(new CustomEvent("virtus:orders-refreshed"));
                    } catch (e) {
                        const detail = e.response?.data?.detail || `Order ${n} not found.`;
                        setHistory((h) => [...h, { role: "assistant", text: detail }]);
                        speak(detail);
                    }
                })();
                return true;
            }

            // Mark delivered
            const deliveredMatch = s.match(DELIVERED_RE);
            if (deliveredMatch) {
                const n = parseInt(deliveredMatch[1], 10);
                (async () => {
                    try {
                        const { data } = await api.post(
                            `/logistics/orders/by-index/${n}/mark-delivered`
                        );
                        const msg = `Order ${n}, ${data.item}, marked delivered.`;
                        setHistory((h) => [...h, { role: "assistant", text: msg }]);
                        speak(msg);
                        window.dispatchEvent(new CustomEvent("virtus:orders-refreshed"));
                    } catch (e) {
                        const detail = e.response?.data?.detail || `Order ${n} not found.`;
                        setHistory((h) => [...h, { role: "assistant", text: detail }]);
                        speak(detail);
                    }
                })();
                return true;
            }

            // Add commands (deep-link to page with intent)
            if (ADD_ORDER_RE.test(s)) {
                navigate("/logistics?add=1");
                const ack = "Opening new order.";
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }
            if (ADD_VESSEL_RE.test(s)) {
                navigate("/fleet");
                const ack = "Opening fleet.";
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }
            if (ADD_TRIAL_RE.test(s)) {
                navigate("/trials");
                const ack = "Opening trials.";
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }
            if (ADD_DEAL_RE.test(s)) {
                navigate("/bd");
                const ack = "Opening BD pipeline.";
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }

            // Close overlays
            if (CLOSE_RE.test(s)) {
                window.dispatchEvent(new CustomEvent("virtus:collapse-task"));
                window.dispatchEvent(new CustomEvent("virtus:collapse-order"));
                window.dispatchEvent(new CustomEvent("virtus:collapse-vessel"));
                window.dispatchEvent(new CustomEvent("virtus:collapse-trial"));
                window.dispatchEvent(new CustomEvent("virtus:collapse-deal"));
                const ack = "Overview closed.";
                setHistory((h) => [...h, { role: "assistant", text: ack }]);
                speak(ack);
                return true;
            }

            // Brief
            if (BRIEF_RE.test(s)) {
                runBriefingRef.current && runBriefingRef.current({ auto: false });
                return true;
            }

            return false;
        },
        [speak, navigate]
    );

    const handleTranscript = useCallback(
        async (spoken) => {
            if (!spoken || !spoken.trim()) {
                setState("idle");
                return;
            }
            setHistory((h) => [...h, { role: "user", text: spoken }]);
            if (tryHandleCommand(spoken)) return;

            setState("thinking");
            try {
                const { data } = await api.post("/voice/chat", {
                    message: spoken,
                    session_id: sessionRef.current,
                });
                sessionRef.current = data.session_id;
                setHistory((h) => [...h, { role: "assistant", text: data.text }]);
                speak(data.text);
            } catch (e) {
                const msg = "Chat error. Check backend connection.";
                setHistory((h) => [...h, { role: "assistant", text: msg }]);
                setState("idle");
                toast.error(msg);
            }
        },
        [speak, tryHandleCommand]
    );

    // -- Recording (mic capture via selected input)
    const startRecording = useCallback(async () => {
        // Hard-cut any active VIRTUS speech so it doesn't bleed into the mic.
        stopSpeech();
        // Small delay so the speaker stream releases before we open the mic.
        await new Promise((r) => setTimeout(r, 150));
        try {
            const inputId = getSelectedInputId();
            const constraints = {
                audio: inputId ? { deviceId: { exact: inputId } } : true,
            };
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            } catch (e) {
                if (e.name === "OverconstrainedError") {
                    // Selected mic went away — fall back.
                    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                } else {
                    throw e;
                }
            }
            const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : "audio/webm";
            const mr = new MediaRecorder(stream, { mimeType: mime });
            chunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                if (chunksRef.current.length === 0) {
                    setState("idle");
                    return;
                }
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                const form = new FormData();
                form.append("file", blob, "voice.webm");
                setState("thinking");
                try {
                    const { data } = await api.post("/voice/transcribe", form, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    await handleTranscript(data.text);
                } catch (e) {
                    setState("idle");
                    toast.error("Transcription failed.");
                }
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setState("recording");
            setExpanded(true);
            setTimeout(() => {
                if (mr.state === "recording") mr.stop();
            }, 5000);
        } catch (e) {
            toast.error("Microphone permission denied.");
            setState("idle");
        }
    }, [handleTranscript, stopSpeech]);

    // -- Wake word (Web Speech API, uses OS default mic)
    useEffect(() => {
        if (!wakeArmed) return;
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            toast.warning("Wake-word not supported in this browser. Click the orb to talk.");
            setWakeArmed(false);
            return;
        }
        const recog = new SR();
        recog.continuous = true;
        recog.interimResults = true;
        recog.lang = "en-US";
        let lastActivity = Date.now();
        let stopped = false;

        const safeStart = () => {
            if (stopped) return;
            try {
                recog.start();
            } catch (_) {
                // start() throws if already started — ignore.
            }
        };

        recog.onresult = (e) => {
            lastActivity = Date.now();
            for (let i = e.resultIndex; i < e.results.length; i++) {
                const t = e.results[i][0].transcript.trim().toLowerCase();
                if (t.includes("hey virtus") || t.includes("hey virtues")) {
                    try {
                        recog.stop();
                    } catch (_) {}
                    // Cut any speaking briefing before capturing user input.
                    stopSpeech();
                    startRecording();
                    return;
                }
            }
        };
        recog.onerror = (e) => {
            // Ignore benign errors ("no-speech", "aborted") and let onend restart.
            // For "audio-capture" and "not-allowed" we stay off — user must reconnect mic.
            if (e && (e.error === "not-allowed" || e.error === "service-not-allowed")) {
                stopped = true;
                setWakeArmed(false);
                toast.warning("Microphone blocked. Grant permission and re-arm.");
            }
        };
        recog.onstart = () => {
            lastActivity = Date.now();
        };
        recog.onend = () => {
            if (stopped) return;
            if (state !== "recording") {
                // Brief backoff before restart so Chrome doesn't throw.
                setTimeout(safeStart, 250);
            }
        };
        safeStart();
        wakeRecogRef.current = recog;
        setState("listening");

        // Watchdog: if the recogniser stalls for > 45s of silence with no restart,
        // force a stop/start cycle. Also recover when the tab regains focus.
        const watchdog = setInterval(() => {
            if (stopped) return;
            if (state === "recording" || state === "speaking" || state === "thinking") {
                lastActivity = Date.now();
                return;
            }
            if (Date.now() - lastActivity > 45000) {
                lastActivity = Date.now();
                try {
                    recog.stop();
                } catch (_) {}
                // onend will safeStart it back.
            }
        }, 10000);

        const onVisibility = () => {
            if (document.visibilityState === "visible" && !stopped) {
                lastActivity = Date.now();
                try {
                    recog.stop();
                } catch (_) {}
            }
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            stopped = true;
            clearInterval(watchdog);
            document.removeEventListener("visibilitychange", onVisibility);
            try {
                recog.stop();
            } catch (_) {}
            wakeRecogRef.current = null;
            setState("idle");
        };
    }, [wakeArmed, startRecording, state, stopSpeech]);

    // -- Morning briefing
    const runBriefingRef = useRef(null);
    const briefingCooldownRef = useRef(0);
    const runBriefing = useCallback(
        async ({ auto = false } = {}) => {
            // Hard lock: never overlap a briefing with itself or with active speech.
            if (briefingActiveRef.current || busyRef.current) {
                if (!auto) toast.info("Briefing already in progress.");
                return;
            }
            // Failure cooldown: if the last briefing failed, block retries for 3s
            // so a burst of clicks does not queue multiple failed briefings.
            if (Date.now() < briefingCooldownRef.current) {
                if (!auto)
                    toast.info("Briefing recovering — try again in a moment.");
                return;
            }
            briefingActiveRef.current = true;
            setExpanded(true);
            setBriefingActive(true);
            setState("thinking");
            setHistory((h) => [
                ...h,
                {
                    role: "user",
                    text: auto ? "[10:00 // MORNING BRIEF — AUTO]" : "[MORNING BRIEF — MANUAL]",
                },
            ]);
            try {
                const { data } = await api.post("/voice/briefing", {});
                setHistory((h) => [...h, { role: "assistant", text: data.text }]);
                await speak(data.text, () => {
                    briefingActiveRef.current = false;
                    setBriefingActive(false);
                });
                if (auto) {
                    try { sessionStorage.setItem(BRIEFING_KEY, new Date().toDateString()); } catch (_) {}
                }
            } catch (e) {
                briefingActiveRef.current = false;
                setBriefingActive(false);
                setState("idle");
                briefingCooldownRef.current = Date.now() + 3000;
                toast.error("Briefing failed. Check backend connection.");
            }
        },
        [speak]
    );
    useEffect(() => {
        runBriefingRef.current = runBriefing;
    }, [runBriefing]);

    // 10:00 daily scheduler
    useEffect(() => {
        const tick = () => {
            if (briefingActiveRef.current || busyRef.current) return;
            const now = new Date();
            if (now.getHours() !== BRIEFING_HOUR) return;
            if (now.getMinutes() !== BRIEFING_MINUTE) return;
            let already = null;
            try { already = sessionStorage.getItem(BRIEFING_KEY); } catch (_) {}
            if (already === now.toDateString()) return;
            runBriefing({ auto: true });
        };
        const id = setInterval(tick, 30 * 1000);
        return () => clearInterval(id);
    }, [runBriefing]);

    const sendText = async () => {
        if (!text.trim()) return;
        const t = text.trim();
        setText("");
        setExpanded(true);
        await handleTranscript(t);
    };

    const orbColor = {
        idle: "bg-virtus-surface border-virtus-line",
        listening: "bg-virtus-surface border-virtus-orange",
        recording: "bg-virtus-orange border-virtus-orange",
        thinking: "bg-virtus-surface border-virtus-working",
        speaking: "bg-virtus-surface border-virtus-done",
    }[state];

    const statusText = {
        idle: "IDLE",
        listening: 'AWAITING "HEY VIRTUS"',
        recording: "RECORDING",
        thinking: "ANALYZING",
        speaking: briefingActive ? "10:00 BRIEFING" : "TRANSMITTING",
    }[state];

    return (
        <div
            className="fixed bottom-6 right-6 z-[70] flex flex-col items-end gap-3"
            data-testid={TID.voiceOrb}
        >
            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="w-[380px] max-h-[480px] flex flex-col bg-[#0A0A0A] border border-virtus-orange"
                        data-testid={TID.voicePanel}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-virtus-line">
                            <div className="flex items-center gap-2">
                                <span className="inline-block h-2 w-2 bg-virtus-orange pulse-dot" />
                                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                                    VIRTUS // {statusText}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                {briefingActive ? (
                                    <button
                                        onClick={stopSpeech}
                                        className="flex items-center gap-1 px-2 py-1 font-mono text-[9px] tracking-[0.22em] uppercase border border-virtus-stuck text-virtus-stuck hover:bg-virtus-stuck hover:text-white"
                                        data-testid={TID.voiceBriefingStopBtn}
                                    >
                                        <Square size={10} /> STOP
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => runBriefing({ auto: false })}
                                        disabled={state === "thinking" || state === "speaking"}
                                        className="flex items-center gap-1 px-2 py-1 font-mono text-[9px] tracking-[0.22em] uppercase border border-virtus-orange text-virtus-orange hover:bg-virtus-orange hover:text-black disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid={TID.voiceBriefingBtn}
                                    >
                                        <Radio size={10} /> 10:00 BRIEF
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowAudioSettings(true)}
                                    className="text-virtus-dim hover:text-virtus-orange"
                                    data-testid="voice-audio-settings-btn"
                                    title="Audio devices"
                                    aria-label="Audio devices"
                                >
                                    <Settings size={13} />
                                </button>
                                <button
                                    onClick={() => setExpanded(false)}
                                    className="text-virtus-dim hover:text-virtus-orange"
                                    data-testid={TID.voiceCloseBtn}
                                    aria-label="Close voice panel"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                            {history.length === 0 && (
                                <div className="font-mono text-xs text-virtus-faint leading-relaxed">
                                    &gt; Say "Hey Virtus" or type below.
                                    <br />
                                    &gt; "expand task N" · "expand vessel N" · "expand deal N"
                                    <br />
                                    &gt; "track order N" · "advance deal N" · "close deal N as won"
                                    <br />
                                    &gt; "show fleet" · "show trials" · "show BD pipeline"
                                    <br />
                                    &gt; "run the brief" · "add order" · "add vessel"
                                </div>
                            )}
                            {history.map((m, i) => (
                                <div key={m._k || `msg-${i}`} className="text-sm">
                                    <div
                                        className={`font-mono text-[9px] tracking-[0.22em] uppercase mb-1 ${
                                            m.role === "user"
                                                ? "text-virtus-orange"
                                                : "text-virtus-done"
                                        }`}
                                    >
                                        {m.role === "user" ? "OPERATOR" : "VIRTUS"}
                                    </div>
                                    <div className="text-white leading-relaxed">{m.text}</div>
                                </div>
                            ))}
                            {state === "thinking" && (
                                <div className="flex items-center gap-2 text-virtus-working font-mono text-xs">
                                    <Loader2 size={12} className="animate-spin" />
                                    processing
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 px-4 py-3 border-t border-virtus-line">
                            <input
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && sendText()}
                                placeholder="Type command..."
                                className="flex-1 bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-sm text-white font-mono outline-none"
                                data-testid={TID.voiceTextInput}
                            />
                            <button
                                onClick={sendText}
                                className="p-2 bg-virtus-orange text-black hover:bg-white transition-colors"
                                data-testid={TID.voiceTextSend}
                                aria-label="Send text"
                            >
                                <Send size={14} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="flex items-center gap-3">
                <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim">
                    {statusText}
                </div>
                <button
                    onClick={() => {
                        if (state === "recording") {
                            mediaRecorderRef.current?.stop();
                            return;
                        }
                        // If speaking, first tap = stop the speech. Second tap = record.
                        if (state === "speaking" || briefingActiveRef.current) {
                            stopSpeech();
                            return;
                        }
                        if (!expanded) setExpanded(true);
                        if (!wakeArmed) setWakeArmed(true);
                        else startRecording();
                    }}
                    className={`relative flex items-center justify-center w-16 h-16 border ${orbColor} transition-colors`}
                    data-testid={TID.voiceOrbToggle}
                    aria-label="Toggle voice"
                >
                    {(state === "listening" || state === "recording") && (
                        <span className="absolute inset-0 border border-virtus-orange ripple" />
                    )}
                    <Mic
                        size={22}
                        className={
                            state === "recording"
                                ? "text-black"
                                : state === "idle"
                                ? "text-virtus-dim"
                                : "text-virtus-orange"
                        }
                    />
                </button>
            </div>

            <AudioSettingsModal
                open={showAudioSettings}
                onClose={() => setShowAudioSettings(false)}
            />
        </div>
    );
}
